import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Card, Dropdown, Form, Nav } from 'react-bootstrap';
import { useSearchParams } from 'react-router-dom';
import { ClientSelectGroup } from '@/components/ClientSelectGroup';
import { DeployBlockDialog } from '@/components/clientFlags/ClientTagDialog';
import { ConfirmDialog } from '@/components/deployments/ConfirmDialog';
import { HistoryTable } from '@/components/deployments/HistoryTable';
import { LocksTable } from '@/components/deployments/LocksTable';
import { RefreshButton } from '@/components/deployments/RefreshButton';
import { RunActionDialog, type RunAction } from '@/components/deployments/RunActionDialog';
import { StartCard, type StartPreset, type StartedRun } from '@/components/deployments/StartCard';
import { PageHeader } from '@/components/ui/PageHeader';
import { useDeployFailures } from '@/hooks/useDeployFailures';
import { useDeployPlans } from '@/hooks/useDeployPlans';
import { clientService } from '@/services/clientService';
import { getConfigValue } from '@/services/configService';
import {
  getDeploymentById,
  listDeploymentLocks,
  listDeploymentsByTimeRange,
  listDeploymentsForClient,
  listDeploymentsForGroup,
  listLiveDeployments,
  listRecentGroupRuns,
  releaseDeploymentLock,
  type DeploymentLockRecord,
  type DeploymentRecord,
  type ListByTimeRangeResult,
} from '@/services/deploymentService';
import { getEcrService } from '@/services/ecrService';
import type { Client, ECRImage } from '@/types';
import { retryUnavailableFailures } from '@/utils/deployFailureStore';
import { retryUnavailablePlanResults } from '@/utils/deployPlanStore';
import {
  isFailed,
  isFinishedPlan,
  isGroupMember,
  isGroupSummary,
  isLive,
  newestRunByClient,
  statusKey,
  type ImagesByChannel,
} from '@/utils/deployStatus';
import '@/components/deployments/deployments.css';

type Period = '7d' | '14d' | '30d' | '90d';
type StatusFilter = 'all' | 'failed' | 'running';
type TabKey = 'history' | 'groups' | 'locks';

const PERIOD_DAYS: Record<Period, number> = { '7d': 7, '14d': 14, '30d': 30, '90d': 90 };
const PAGE_SIZE = 100;
/** Rows wanted on screen before a load stops reading pages. */
const VISIBLE_TARGET = 50;
/** The most pages one load reads. A fleet run and its second pass are about 400 rows. */
const MAX_PAGES_PER_LOAD = 8;
const LIVE_REFRESH_MS = 10_000;
/** The most live runs read one by one on each refresh. A fleet run's members come from one read for the group. */
const MAX_LIVE_READS = 15;
const MAX_LIVE_GROUPS = 4;

const sameRecord = (a: DeploymentRecord, b: DeploymentRecord) => JSON.stringify(a) === JSON.stringify(b);
const newestFirst = (a: DeploymentRecord, b: DeploymentRecord) => (b.startedAt || '').localeCompare(a.startedAt || '');

/**
 * Put fresh copies of runs into a list. A run already listed is replaced where
 * it sits. A run not listed is added only when `accept` says it belongs. The
 * same list comes back when nothing changed, so the page does not re-render.
 */
function mergeRecords(
  list: DeploymentRecord[],
  updates: DeploymentRecord[],
  accept?: (record: DeploymentRecord) => boolean
): DeploymentRecord[] {
  if (updates.length === 0) return list;
  const fresh = new Map(updates.map((record) => [record.deploymentId, record]));
  let changed = false;
  const next = list.map((record) => {
    const update = fresh.get(record.deploymentId);
    if (!update) return record;
    fresh.delete(record.deploymentId);
    if (sameRecord(record, update)) return record;
    changed = true;
    return update;
  });
  const added = accept ? [...fresh.values()].filter(accept) : [];
  if (added.length > 0) {
    changed = true;
    next.push(...added);
    next.sort(newestFirst);
  }
  return changed ? next : list;
}

export default function Deployments() {
  const [searchParams, setSearchParams] = useSearchParams();
  const deploymentsTable = getConfigValue('DEPLOYMENTS_TABLE');

  const [clients, setClients] = useState<Client[]>([]);
  const [imagesByChannel, setImagesByChannel] = useState<ImagesByChannel>({ 'numa-deploy': [], 'numa-deploy-dev': [] });
  const [baseLoading, setBaseLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // `history` is every run in the period, newest first.
  const [history, setHistory] = useState<DeploymentRecord[]>([]);
  // `pinned` is every run that was live when the list was read, whatever the period. A live run is
  // never off the list by accident: a dead run from before the period shows until somebody ends it.
  const [pinned, setPinned] = useState<DeploymentRecord[]>([]);
  // The run just started here: its row is scrolled into view and marked for a few seconds.
  const [highlightId, setHighlightId] = useState<string | null>(null);
  const [historyNextKey, setHistoryNextKey] = useState<Record<string, unknown> | undefined>();
  // `clientRows` is one client's runs, read by client, when the history is filtered to that client.
  const [clientRows, setClientRows] = useState<DeploymentRecord[]>([]);
  const [clientNextKey, setClientNextKey] = useState<Record<string, unknown> | undefined>();
  const [groupRuns, setGroupRuns] = useState<DeploymentRecord[]>([]);
  const [groupMembers, setGroupMembers] = useState<Record<string, DeploymentRecord[]>>({});
  const [historyLoading, setHistoryLoading] = useState(true);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [refreshedAt, setRefreshedAt] = useState<number | null>(null);

  const [locks, setLocks] = useState<DeploymentLockRecord[]>([]);
  const [locksLoading, setLocksLoading] = useState(false);
  const [locksError, setLocksError] = useState<string | null>(null);
  const [releasing, setReleasing] = useState<DeploymentLockRecord | null>(null);
  const [releasingBusy, setReleasingBusy] = useState(false);

  const [period, setPeriod] = useState<Period>('7d');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [showMembers, setShowMembers] = useState(false);
  const [tab, setTab] = useState<TabKey>('history');
  const [clientFilterOpen, setClientFilterOpen] = useState(false);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [preset, setPreset] = useState<StartPreset | null>(null);
  const [action, setAction] = useState<RunAction | null>(null);
  // The client whose do-not-deploy block a row's menu asked to set or lift.
  const [blockingClient, setBlockingClient] = useState<string | null>(null);

  // `/deployments?client=<name>` filters the history to one client. The filter lives in the address so it can be linked.
  const clientFilter = searchParams.get('client') || 'all';
  const filteredToClient = clientFilter !== 'all';
  const setClientFilter = (value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value === 'all' || !value) next.delete('client');
    else next.set('client', value);
    setSearchParams(next, { replace: true });
  };

  // `/deployments?image=<tag>&channel=<channel>` picks that channel and image in the start form
  // and brings the form into view.
  useEffect(() => {
    const image = searchParams.get('image');
    const channel = searchParams.get('channel');
    if (!image && !channel) return;
    setPreset({
      image: image || undefined,
      channel: channel === 'numa-deploy' || channel === 'numa-deploy-dev' ? channel : undefined,
    });
    // Take the two values out of the address, so a page reload does not pick them again over a later choice.
    const next = new URLSearchParams(searchParams);
    next.delete('image');
    next.delete('channel');
    setSearchParams(next, { replace: true });
  }, [searchParams]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [allClients, prodImages, devImages] = await Promise.all([
          clientService.getAllClients(),
          getEcrService('numa-deploy').getAllImages(),
          getEcrService('numa-deploy-dev')
            .getAllImages()
            .catch(() => [] as ECRImage[]),
        ]);
        if (cancelled) return;
        setClients(allClients);
        setImagesByChannel({ 'numa-deploy': prodImages, 'numa-deploy-dev': devImages });
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Could not load the clients and images.');
      } finally {
        if (!cancelled) setBaseLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const fromIso = useCallback(
    () => new Date(Date.now() - PERIOD_DAYS[period] * 24 * 60 * 60 * 1000).toISOString(),
    [period]
  );

  /**
   * Read pages until the list on screen is full. After a fleet run the first
   * hundred rows are all members of the group, which the history hides, so one
   * page would leave the table looking empty.
   */
  const readPages = useCallback(
    async (
      fetchPage: (key?: Record<string, unknown>) => Promise<ListByTimeRangeResult>,
      startKey: Record<string, unknown> | undefined,
      countsAsVisible: (record: DeploymentRecord) => boolean
    ) => {
      let items: DeploymentRecord[] = [];
      let key = startKey;
      let pages = 0;
      do {
        const page = await fetchPage(key);
        items = items.concat(page.items);
        key = page.lastEvaluatedKey;
        pages += 1;
      } while (key && pages < MAX_PAGES_PER_LOAD && items.filter(countsAsVisible).length < VISIBLE_TARGET);
      return { items, key };
    },
    []
  );

  const memberHidden = useCallback((record: DeploymentRecord) => !showMembers && isGroupMember(record), [showMembers]);

  // A filter can change while a load is in flight. Only the newest load may write its result.
  const loadSeq = useRef(0);

  const loadHistory = useCallback(async () => {
    if (!deploymentsTable) return;
    const seq = ++loadSeq.current;
    setHistoryLoading(true);
    setHistoryError(null);
    try {
      const from = fromIso();
      const [all, groups, liveNow, forClient] = await Promise.all([
        readPages(
          (key) => listDeploymentsByTimeRange({ fromIso: from, pageSize: PAGE_SIZE, exclusiveStartKey: key }),
          undefined,
          (record) => !memberHidden(record)
        ),
        listRecentGroupRuns(),
        // Not worth failing the page for: without it only a live run from before the period is missed.
        listLiveDeployments().catch((e) => {
          console.warn('Could not read the live runs from before the period', e);
          return [] as DeploymentRecord[];
        }),
        filteredToClient
          ? readPages(
              (key) =>
                listDeploymentsForClient({
                  clientName: clientFilter,
                  fromIso: from,
                  pageSize: PAGE_SIZE,
                  exclusiveStartKey: key,
                }),
              undefined,
              () => true
            )
          : Promise.resolve(null),
      ]);
      if (seq !== loadSeq.current) return;
      setHistory(all.items);
      setHistoryNextKey(all.key);
      setPinned(liveNow);
      setGroupRuns(groups);
      setClientRows(forClient?.items ?? []);
      setClientNextKey(forClient?.key);
      setRefreshedAt(Date.now());
    } catch (e) {
      console.error('Failed to load deployment history', e);
      if (seq === loadSeq.current) {
        setHistoryError(e instanceof Error ? e.message : 'The read failed.');
      }
    } finally {
      if (seq === loadSeq.current) setHistoryLoading(false);
    }
  }, [deploymentsTable, fromIso, readPages, memberHidden, filteredToClient, clientFilter]);

  useEffect(() => {
    void loadHistory();
  }, [loadHistory]);

  const loadMore = async () => {
    setLoadingMore(true);
    try {
      const from = fromIso();
      if (filteredToClient) {
        if (!clientNextKey) return;
        const more = await readPages(
          (key) =>
            listDeploymentsForClient({
              clientName: clientFilter,
              fromIso: from,
              pageSize: PAGE_SIZE,
              exclusiveStartKey: key,
            }),
          clientNextKey,
          () => true
        );
        setClientRows((prev) => [...prev, ...more.items]);
        setClientNextKey(more.key);
      } else {
        if (!historyNextKey) return;
        const more = await readPages(
          (key) => listDeploymentsByTimeRange({ fromIso: from, pageSize: PAGE_SIZE, exclusiveStartKey: key }),
          historyNextKey,
          (record) => !memberHidden(record)
        );
        setHistory((prev) => [...prev, ...more.items]);
        setHistoryNextKey(more.key);
      }
    } catch (e) {
      console.error('Failed to load more history', e);
      setHistoryError(e instanceof Error ? e.message : 'The read failed.');
    } finally {
      setLoadingMore(false);
    }
  };

  // The lock list is a scan of the whole table, so it is never read on a timer: once when
  // the page opens (for the count on the tab), when the Locks tab opens, and on Refresh.
  const loadLocks = useCallback(async () => {
    if (!deploymentsTable) return;
    setLocksLoading(true);
    setLocksError(null);
    try {
      setLocks(await listDeploymentLocks());
    } catch (e) {
      console.error('Failed to load deployment locks', e);
      setLocksError(e instanceof Error ? e.message : 'The read failed.');
    } finally {
      setLocksLoading(false);
    }
  }, [deploymentsTable]);

  useEffect(() => {
    void loadLocks();
  }, [loadLocks]);

  const refreshAll = () => {
    retryUnavailableFailures();
    retryUnavailablePlanResults();
    void loadHistory();
    void loadLocks();
  };

  const selectTab = (key: string | null) => {
    if (!key) return;
    setTab(key as TabKey);
    if (key === 'locks') void loadLocks();
  };

  // ── Live runs ────────────────────────────────────────────────────────────
  // Every run the page knows to be live: in the period's list, or found live from before it.
  const liveRuns = useMemo(() => {
    const byId = new Map<string, DeploymentRecord>();
    for (const record of [...history, ...pinned]) {
      if (isLive(record) && !isGroupSummary(record) && !isGroupMember(record) && !byId.has(record.deploymentId)) {
        byId.set(record.deploymentId, record);
      }
    }
    return [...byId.values()].sort(newestFirst);
  }, [history, pinned]);
  const liveGroups = useMemo(() => {
    const byId = new Map<string, DeploymentRecord>();
    for (const record of [...groupRuns, ...history, ...pinned]) {
      if (isGroupSummary(record) && isLive(record) && !byId.has(record.deploymentId)) {
        byId.set(record.deploymentId, record);
      }
    }
    return [...byId.values()].sort(newestFirst);
  }, [groupRuns, history, pinned]);
  const anyLive = liveRuns.length > 0 || liveGroups.length > 0;

  const liveClients = useMemo(() => {
    const names = new Set<string>();
    for (const record of [...history, ...pinned]) {
      if (isLive(record) && !isGroupSummary(record)) names.add(record.clientName);
    }
    for (const members of Object.values(groupMembers)) {
      for (const member of members) if (isLive(member)) names.add(member.clientName);
    }
    return names;
  }, [history, pinned, groupMembers]);

  // The timer reads the newest lists without restarting each time they change.
  const latest = useRef({ history, clientRows, groupRuns, liveRuns, liveGroups, clientFilter, filteredToClient });
  latest.current = { history, clientRows, groupRuns, liveRuns, liveGroups, clientFilter, filteredToClient };

  /**
   * Bring the live runs up to date: each live run's own record, each live
   * group's summary and members, and any run started since the list was read.
   */
  const refreshLive = useCallback(async () => {
    const now = latest.current;
    const updates: DeploymentRecord[] = [];
    const members: Record<string, DeploymentRecord[]> = {};
    try {
      await Promise.all([
        ...now.liveRuns.slice(0, MAX_LIVE_READS).map(async (run) => {
          const record = await getDeploymentById(run.deploymentId, false);
          if (record) updates.push(record);
        }),
        ...now.liveGroups.slice(0, MAX_LIVE_GROUPS).map(async (group) => {
          const [summary, rows] = await Promise.all([
            getDeploymentById(group.deploymentId, false),
            listDeploymentsForGroup(group.deploymentId),
          ]);
          if (summary) updates.push(summary);
          members[group.deploymentId] = rows;
          updates.push(...rows);
        }),
      ]);

      // Runs started since the list was read. Two minutes of overlap covers a starter whose clock runs behind.
      const newest = now.history[0]?.startedAt;
      let started: DeploymentRecord[] = [];
      if (newest) {
        const since = new Date(Date.parse(newest) - 2 * 60 * 1000).toISOString();
        started = (await listDeploymentsByTimeRange({ fromIso: since, pageSize: PAGE_SIZE, legacyFallback: false }))
          .items;
      }

      const startedIds = new Set(started.map((record) => record.deploymentId));
      const all = [...updates, ...started];
      setHistory((prev) => mergeRecords(prev, all, (record) => startedIds.has(record.deploymentId)));
      // A pinned run keeps its row when it ends, so the person who ended it sees the result.
      setPinned((prev) => mergeRecords(prev, all));
      setGroupRuns((prev) => mergeRecords(prev, all.filter(isGroupSummary), () => true));
      if (now.filteredToClient) {
        setClientRows((prev) => mergeRecords(prev, all, (record) => record.clientName === now.clientFilter));
      }
      if (Object.keys(members).length > 0) setGroupMembers((prev) => ({ ...prev, ...members }));
      setRefreshedAt(Date.now());
    } catch (e) {
      // One missed refresh is not worth an alert. The next one is ten seconds away.
      console.warn('Could not refresh the live runs', e);
    }
  }, []);

  // Status and duration used to freeze until somebody pressed Refresh. While a run is live the
  // page now reads it every ten seconds, and stops when the browser tab is hidden.
  useEffect(() => {
    if (!anyLive) return;
    const tick = () => {
      if (document.visibilityState === 'visible') void refreshLive();
    };
    const timer = setInterval(tick, LIVE_REFRESH_MS);
    document.addEventListener('visibilitychange', tick);
    // A group run's counts need its members, which only this refresh reads. Do not wait ten seconds for the first.
    if (latest.current.liveGroups.length > 0) tick();
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', tick);
    };
  }, [anyLive, refreshLive]);

  // ── The table ────────────────────────────────────────────────────────────
  const tableRows = useMemo(() => {
    const byId = new Map<string, DeploymentRecord>();
    const add = (record: DeploymentRecord) => {
      if (!byId.has(record.deploymentId)) byId.set(record.deploymentId, record);
    };
    // The period's list...
    (filteredToClient ? clientRows : history).forEach(add);
    // ...and every live run, whatever the period. With group members shown, each running member of a
    // live group has a row of its own, even when the period's pages have not reached it.
    pinned.forEach(add);
    liveGroups.forEach(add);
    if (showMembers || filteredToClient) {
      for (const group of liveGroups) (groupMembers[group.deploymentId] || []).filter(isLive).forEach(add);
    }

    let rows = [...byId.values()];
    // One client's runs are mostly members of fleet runs. Hiding them would hide the client's history.
    if (filteredToClient) rows = rows.filter((record) => record.clientName === clientFilter);
    else rows = rows.filter((record) => !memberHidden(record));
    if (statusFilter === 'running') rows = rows.filter(isLive);
    else if (statusFilter === 'failed') rows = rows.filter(isFailed);
    // Live runs are pinned to the top. The rest are newest first.
    return rows.sort((a, b) => Number(isLive(b)) - Number(isLive(a)) || newestFirst(a, b));
  }, [
    filteredToClient,
    clientFilter,
    clientRows,
    history,
    pinned,
    liveGroups,
    groupMembers,
    showMembers,
    memberHidden,
    statusFilter,
  ]);

  const newestByClient = useMemo(
    () => newestRunByClient([...history, ...clientRows, ...pinned]),
    [history, clientRows, pinned]
  );

  // Read the reason for the failed rows on screen, and for a resolved row once it is opened.
  const rowsNeedingReason = useMemo(
    () =>
      tableRows.filter(
        (record) => isFailed(record) || (statusKey(record) === 'resolved' && expanded[record.deploymentId])
      ),
    [tableRows, expanded]
  );
  const failures = useDeployFailures(tab === 'history' ? rowsNeedingReason : [], expanded);

  // What each plan-only run on screen found. Its status cannot say, so it is read from the log.
  const planRows = useMemo(() => tableRows.filter(isFinishedPlan), [tableRows]);
  const plans = useDeployPlans(tab === 'history' ? planRows : []);

  const hasMore = filteredToClient ? Boolean(clientNextKey) : Boolean(historyNextKey);
  const toggleExpanded = (id: string) => setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));

  const emptyText = (() => {
    const days = PERIOD_DAYS[period];
    const who = filteredToClient ? ` for ${clientFilter}` : '';
    if (statusFilter === 'failed') return `No failed deployment${who} in the last ${days} days.`;
    if (statusFilter === 'running') return `No deployment${who} is running.`;
    return `No deployment${who} in the last ${days} days.`;
  })();

  // The new run's row goes to the top of the history (live rows are pinned there). Make sure no filter
  // hides it, then bring it into view and mark it for a few seconds.
  const highlightTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => void (highlightTimer.current && clearTimeout(highlightTimer.current)), []);
  const handleStarted = (run: StartedRun) => {
    setTab('history');
    if (statusFilter === 'failed') setStatusFilter('all');
    if (filteredToClient && (run.kind === 'group' || run.clientName !== clientFilter)) setClientFilter('all');
    setHighlightId(run.kind === 'group' ? run.groupRunId : run.deploymentId);
    if (highlightTimer.current) clearTimeout(highlightTimer.current);
    highlightTimer.current = setTimeout(() => setHighlightId(null), 8000);
    void loadHistory();
  };

  const handleActionDone = (done: RunAction) => {
    setAction(null);
    void loadHistory();
    // Stop and Mark failed free the client's lock.
    if (done.type === 'stop' || done.type === 'mark-failed') void loadLocks();
  };

  const confirmRelease = async () => {
    if (!releasing) return;
    setReleasingBusy(true);
    try {
      await releaseDeploymentLock(releasing.clientName);
      setReleasing(null);
      await loadLocks();
    } catch (e) {
      console.error('Failed to release lock:', e);
      setLocksError(e instanceof Error ? e.message : 'Could not release the lock.');
      setReleasing(null);
    } finally {
      setReleasingBusy(false);
    }
  };

  const handlers = {
    onRunAgain: (record: DeploymentRecord) => setAction({ type: 'run-again', record }),
    onStop: (record: DeploymentRecord) => setAction({ type: 'stop', record }),
    onMarkFailed: (record: DeploymentRecord) => setAction({ type: 'mark-failed', record }),
    onMarkResolved: (record: DeploymentRecord) => setAction({ type: 'mark-resolved', record }),
    onBlockDeploys: (record: DeploymentRecord) => setBlockingClient(record.clientName),
  };

  const actionNewest = action ? newestByClient.get(action.record.clientName) : undefined;

  return (
    <div className="deploy-page">
      <PageHeader title="Deployments" />

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <StartCard
        clients={clients}
        imagesByChannel={imagesByChannel}
        loading={baseLoading}
        preset={preset}
        liveClients={liveClients}
        onStarted={handleStarted}
      />

      {!deploymentsTable ? (
        <Alert variant="warning">Deployment history is not set up for this portal.</Alert>
      ) : (
        <section className="deploy-section">
          <div className="deploy-toolbar">
            <Nav variant="pills" className="nav-pills-custom" activeKey={tab} onSelect={selectTab}>
              <Nav.Item>
                <Nav.Link eventKey="history">History</Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="groups">Groups</Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="locks">
                  Locks
                  {locks.length > 0 && <span className="deploy-toolbar__count">{locks.length}</span>}
                </Nav.Link>
              </Nav.Item>
            </Nav>

            <div className="deploy-toolbar__filters">
              {tab === 'history' && (
                <>
                  <Dropdown show={clientFilterOpen} onToggle={(next) => setClientFilterOpen(next)} autoClose="outside">
                    <Dropdown.Toggle variant="outline-secondary" size="sm" id="deploy-client-filter">
                      {filteredToClient ? clientFilter : 'All clients'}
                    </Dropdown.Toggle>
                    <Dropdown.Menu className="deploy-toolbar__client-menu">
                      {clientFilterOpen && (
                        <ClientSelectGroup
                          size="sm"
                          value={clientFilter}
                          onChange={(value) => {
                            setClientFilter(value);
                            setClientFilterOpen(false);
                          }}
                          clients={clients}
                          allOption={{ value: 'all', label: 'All clients' }}
                        />
                      )}
                    </Dropdown.Menu>
                  </Dropdown>

                  <Form.Select
                    size="sm"
                    className="deploy-toolbar__select"
                    value={period}
                    onChange={(e) => setPeriod(e.target.value as Period)}
                    aria-label="Period"
                  >
                    <option value="7d">Last 7 days</option>
                    <option value="14d">Last 14 days</option>
                    <option value="30d">Last 30 days</option>
                    <option value="90d">Last 90 days</option>
                  </Form.Select>

                  <Form.Select
                    size="sm"
                    className="deploy-toolbar__select"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
                    aria-label="Status"
                  >
                    <option value="all">All statuses</option>
                    <option value="failed">Failed</option>
                    <option value="running">Running</option>
                  </Form.Select>

                  <Form.Check
                    type="switch"
                    id="deploy-show-members"
                    className="deploy-toolbar__switch"
                    label="Show group members"
                    checked={showMembers || filteredToClient}
                    disabled={filteredToClient}
                    title={
                      filteredToClient
                        ? 'One client is selected, so its runs inside group runs are always shown.'
                        : 'Show each client of a group run as a row of its own.'
                    }
                    onChange={(e) => setShowMembers(e.currentTarget.checked)}
                  />
                </>
              )}
              <RefreshButton
                onRefresh={refreshAll}
                busy={historyLoading || locksLoading}
                refreshedAt={refreshedAt}
                auto={anyLive}
              />
            </div>
          </div>

          {tab !== 'locks' && historyError && (
            <Alert variant="danger">
              Could not load the deployments: {historyError} Check your connection, then press Refresh.
            </Alert>
          )}

          {tab === 'history' && (
            <Card>
              <HistoryTable
                rows={tableRows}
                images={imagesByChannel}
                failures={failures}
                plans={plans}
                highlightId={highlightId}
                newestByClient={newestByClient}
                liveClients={liveClients}
                groupMembers={groupMembers}
                expanded={expanded}
                onToggle={toggleExpanded}
                loading={historyLoading || baseLoading}
                emptyText={emptyText}
                {...handlers}
              />
              {(tableRows.length > 0 || hasMore) && (
                <div className="deploy-table-foot">
                  <span>
                    {tableRows.length} {tableRows.length === 1 ? 'run' : 'runs'} shown from the last{' '}
                    {PERIOD_DAYS[period]} days{filteredToClient ? ` for ${clientFilter}` : ''}
                    {hasMore ? '. Older runs are not loaded yet.' : '.'}
                  </span>
                  {hasMore && (
                    <Button variant="outline-secondary" size="sm" onClick={loadMore} disabled={loadingMore}>
                      {loadingMore ? 'Loading' : 'Load older runs'}
                    </Button>
                  )}
                </div>
              )}
            </Card>
          )}

          {tab === 'groups' && (
            <>
              <p className="deploy-note">
                The last {groupRuns.length || ''} group runs: one image sent to many clients.
              </p>
              <Card>
                <HistoryTable
                  rows={groupRuns}
                  images={imagesByChannel}
                  failures={{}}
                  groupMembers={groupMembers}
                  expanded={expanded}
                  onToggle={toggleExpanded}
                  loading={historyLoading}
                  emptyText="No group deployment yet."
                  subjectHeading="Group"
                  onStop={handlers.onStop}
                />
              </Card>
            </>
          )}

          {tab === 'locks' && (
            <>
              <p className="deploy-note">
                A lock stops two deploys of one client from running at once. Release one only when the deploy that holds
                it has fully stopped.
              </p>
              {locksError && (
                <Alert variant="danger">Could not read the locks: {locksError} Press Refresh to try again.</Alert>
              )}
              <Card>
                <LocksTable
                  locks={locks}
                  loading={locksLoading}
                  releasingId={releasingBusy && releasing ? releasing.deploymentId : null}
                  onRelease={setReleasing}
                />
              </Card>
            </>
          )}
        </section>
      )}

      <RunActionDialog
        action={action}
        images={imagesByChannel}
        clients={clients.length > 0 ? clients : undefined}
        newerRun={
          action && actionNewest && actionNewest.deploymentId !== action.record.deploymentId ? actionNewest : undefined
        }
        clientBusy={action ? liveClients.has(action.record.clientName) : false}
        failure={action ? failures[action.record.deploymentId] : undefined}
        onClose={() => setAction(null)}
        onDone={handleActionDone}
      />

      <DeployBlockDialog clientName={blockingClient} onClose={() => setBlockingClient(null)} />

      <ConfirmDialog
        show={Boolean(releasing)}
        title="Release this lock?"
        confirmLabel="Release lock"
        variant="danger"
        busy={releasingBusy}
        onCancel={() => setReleasing(null)}
        onConfirm={() => void confirmRelease()}
      >
        <p className="mb-0">
          This frees <strong>{releasing?.clientName}</strong> for a new deploy. Do it only when the deploy that holds
          the lock has fully stopped: with the lock gone, a second deploy can start on top of one that is still running.
        </p>
      </ConfirmDialog>
    </div>
  );
}
